import React, { useState, useEffect, useContext, useRef, useMemo, useCallback } from 'react'
import { Grid, ButtonGroup, Typography} from '@mui/material'
import { AuthContext } from "../auth/Auth.jsx";
import { Stack } from '@mui/system';
import { ScrollableGrid } from './ScrollableGrid.jsx';
import { TypesenseSearch } from '../instasearch/TypesenseSearch.tsx';
import { CollectionHits } from '../instasearch/CollectionHits.tsx';
import { curatedTemplateUserID, getDocByID } from '../libs/firebase.js';
import { CollectionInterface, ProductType } from 'ts-interfaces';
import { useCollectionDetailed } from '../global-state/useCollectionDetailed';
import { useMattoState } from '../MattoState.ts';
import { useLoginState } from '../studioxr/auth/LoginState';
import { ArrowBack } from '@mui/icons-material';
import { SideBarMode } from '../ts/app_interfaces.ts';
import { TabButton } from '../buttons/ButtonStyles.jsx';
import { useLoadProjectFromCollection } from './CollectionLoadProject';
import { ActionButton } from '../../../creator/src/buttons/ButtonStyles.jsx';
import { useProjectSchemas } from '../global-state/useProjectSchemas';
import { ProjectFilter } from './ProjectFilter';
import { SearchFilter } from '../instasearch/SearchFilter';
import { useProjectDataFetcher } from '../hooks/useProjectDataFetcher.ts';
import { useLocation } from 'react-router-dom';



const getNoOfItems = (coll:CollectionInterface) =>   coll.products.length + (coll.userImages ? coll.userImages.length : 0) + (coll.userMaterials ? coll.userMaterials.length : 0)
enum CollectionType { MY = 'my', CURATED = 'curated' }


export function CollectionTab () {
   const {currentUser} = useContext(AuthContext);
   const location = useLocation();
   
   // Subscribe to minimal state for initial logic
   const fetchCollections = useCollectionDetailed(state => state.fetchCollections);
   const initialFetchDone = useCollectionDetailed(state => state.initialFetchDone);
   const getTeamsProAccess = useCollectionDetailed(state => state.getTeamsProAccess);
   
   // Get user's plan type
   const { isProUser, isTeamsProUser } = getTeamsProAccess();
   
   const [collectionType, setCollectionType]=useState<CollectionType>(CollectionType.MY)
   const [selectedCollection, setSelectedCollection]=useState<CollectionInterface | null>(null)
   const [selectedProjectIds, setSelectedProjectIds]=useState<string[]>([])
   const [individualCollectionFilters, setIndividualCollectionFilters]=useState<{myIndividual: boolean, sharedIndividual: boolean}>({myIndividual: false, sharedIndividual: false})
   const rootCollectionContainer = useRef<any>();
   const loadProjectFromCollection = useLoadProjectFromCollection();
   const { fetchProjectData } = useProjectDataFetcher();

   // Initial fetch effect - simplified
   useEffect(() => {
       if (!currentUser?.uid) return;
       
       fetchCollections(currentUser.uid, null);
   }, [currentUser?.uid]);

   // Ensure old collections are loaded for backward compatibility with toggleProductInCollection
   useEffect(() => {
       if (currentUser?.uid && initialFetchDone) {
           // Make sure the old LoginState has collections populated for toggleProductInCollection to work
           const loginState = useLoginState.getState();
           if (loginState.collections.length === 0) {
               loginState.getMoreCollections(20);
           }
       }
   }, [currentUser?.uid, initialFetchDone]); // Remove fetchCollections from deps

   // URL params effect
   useEffect(() => {
       const urlParams = new URLSearchParams(window.location.search);
       const collectionID = urlParams.get('collectionId') // Changed from 'collectionID' to 'collectionId'
       
       if (!collectionID) {
           return;
       }

       const collectionTypeParam = urlParams.get('collectionType')
       if (collectionTypeParam) {
           setCollectionType(collectionTypeParam as CollectionType);
       }

       const fetchBoardData = async (url: string) => {
           if(!url) return;
           const boardId = url.split('/').pop();
           try {
               const data = await fetchProjectData(boardId);
               return data;
           } catch (error) {
               console.error('Error loading board data:', error);
           }
       };
      
       getDocByID('usersMyCollection', collectionID).then((record) => {
           if (!record.exists()) {
               return;
           }
           
           const collection:CollectionInterface = record.data() as CollectionInterface
           
           if (collection) {
               setSelectedCollection(collection);
               if (collection.associatedBoardUrl) {
                   fetchBoardData(collection.associatedBoardUrl).then((projectData) => {
                       loadProjectFromCollection(collection.associatedBoardUrl, true, projectData);
                   });
               }
               else if (collection.projects && collection.projects.length > 0) loadProjectFromCollection(collection.projects[0], true);
           }
           useMattoState.getState().setSideBarMode(SideBarMode.Collections)
       }).catch((error) => {
           console.error('CollectionTab: Error fetching collection from Firebase', { collectionID, error });
       })
   },[location]) // Added location as dependency to re-run when URL changes

   // 🔧 SIMPLIFIED: Only check if initial fetch is done
   const shouldShowCollections = initialFetchDone;
   
   // Get collection edit capability (must be called outside conditionals per rules of hooks)
   const allCollections = useCollectionDetailed(state => state.allCollections);
   const checkEditability = useCollectionDetailed(state => state.checkEditability);
   

   if (!currentUser) return null; 
   
   if (selectedCollection) {
       // Calculate edit capability outside the hook call
       const detailedCollection = allCollections.find(c => c.id === selectedCollection.id);
       const canEditCollection = detailedCollection ? detailedCollection.isEditable : checkEditability(selectedCollection, currentUser?.uid || '');
       
       return (
           <CollectionList 
               selectedCollection={selectedCollection} 
               onClose={() => setSelectedCollection(null)} 
               collectionType={collectionType} 
               canEditCollection={canEditCollection} 
           />
       );
   }
   
   return (
       <Grid container ref={rootCollectionContainer} spacing={1} style={{paddingTop:'17px',paddingLeft:'8px',display:'flex'}} >
           <Grid item xs={12} style={{paddingBottom:'5px',paddingTop:'18px',paddingRight:'15px'}} display='flex' justifyContent="center" >
               <ButtonGroup fullWidth={true} disableElevation variant="text"  >
                   <TabButton className={collectionType == CollectionType.CURATED ? 'selected' : ''} onClick ={ () => { setSelectedCollection(null); setCollectionType(CollectionType.CURATED) }}>Curated</TabButton>
                   <TabButton className={collectionType == CollectionType.MY ? 'selected' : ''} onClick ={ () => { setSelectedCollection(null); setCollectionType(CollectionType.MY) }}>My Collections</TabButton>
               </ButtonGroup>
           </Grid>

           {collectionType === CollectionType.MY && (
               <Grid item xs={12} style={{paddingLeft:'8px',paddingTop:'5px',paddingBottom:'5px'}}>
                   <ActionButton onClick={() => useMattoState.getState().setModal({name:'collection',id:null})} style={{width:'100%'}}>
                       Create New Collection
                   </ActionButton>
               </Grid>
           )}

           {/* Show collections immediately when data is ready */}
           {!shouldShowCollections ? (
               <Grid item xs={12} style={{paddingLeft:'8px',paddingTop:'5px'}}>
                   <div style={{padding: '20px', textAlign: 'center', color: '#666'}}>
                       Loading collections...
                   </div>
               </Grid>
           ) : (
               <CollectionTabContent 
                   collectionType={collectionType}
                   currentUser={currentUser}
                   selectedProjectIds={selectedProjectIds}
                   individualCollectionFilters={individualCollectionFilters}
                   onSelectedProjectIdsChange={setSelectedProjectIds}
                   onIndividualFiltersChange={setIndividualCollectionFilters}
                   onCollectionSelect={setSelectedCollection}
                   rootCollectionContainer={rootCollectionContainer}
                   isProUser={isProUser}
                   isTeamsProUser={isTeamsProUser}
               />
           )}
       </Grid>
   )
}

interface CollectionTabContentProps {
    collectionType: CollectionType;
    currentUser: any; // Replace with your User type
    selectedProjectIds: string[];
    individualCollectionFilters: {
        myIndividual: boolean;
        sharedIndividual: boolean;
    };
    onSelectedProjectIdsChange: (projectIds: string[]) => void;
    onIndividualFiltersChange: (filters: { myIndividual: boolean; sharedIndividual: boolean }) => void;
    onCollectionSelect: (collection: CollectionInterface) => void;
    rootCollectionContainer: React.RefObject<any>;
    isProUser: boolean;
    isTeamsProUser: boolean;
}

// 🔧 MEMOIZED: Prevent unnecessary re-renders of content
const CollectionTabContent = React.memo<CollectionTabContentProps>(function CollectionTabContent({ 
    collectionType, 
    currentUser, 
    selectedProjectIds, 
    individualCollectionFilters,
    onSelectedProjectIdsChange,
    onIndividualFiltersChange,
    onCollectionSelect,
    rootCollectionContainer,
    isProUser,
    isTeamsProUser
}) {

    
    // Single selector for all data to prevent multiple subscriptions
    const storeData = useCollectionDetailed(state => ({
        allCollections: state.allCollections,
        myIndividualCollections: state.myIndividualCollections,
        myProjectCollections: state.myProjectCollections,
        sharedIndividualCollections: state.sharedIndividualCollections,
        sharedProjectCollections: state.sharedProjectCollections,
        curatedCollections: state.curatedCollections,
        curatedCollectionsLoaded: state.curatedCollectionsLoaded,
        getMoreCollections: state.getMoreCollections,
        getMoreCuratedCollections: state.getMoreCuratedCollections,
        fetchCuratedCollections: state.fetchCuratedCollections,
        deleteCollection: state.deleteCollection,
        checkEditability: state.checkEditability
    }));

    const isFetchingRef = useRef(false);

    // Fetch curated collections when needed
    useEffect(() => {
        if (collectionType === CollectionType.CURATED && !storeData.curatedCollectionsLoaded) {
            storeData.fetchCuratedCollections();
        }
    }, [collectionType, storeData.fetchCuratedCollections, storeData.curatedCollectionsLoaded]);

    // Memoized collection arrays
    const projectCollections = useMemo(() => [
        ...storeData.myProjectCollections,
        ...storeData.sharedProjectCollections
    ], [storeData.myProjectCollections, storeData.sharedProjectCollections]);

    const individualCollections = useMemo(() => [
        ...storeData.myIndividualCollections,
        ...storeData.sharedIndividualCollections
    ], [storeData.myIndividualCollections, storeData.sharedIndividualCollections]);

    // Collection editability check
    const canEditOrShareCollection = useCallback((collection: CollectionInterface) => {
        const detailedCollection = storeData.allCollections.find(c => c.id === collection.id);
        if (detailedCollection) {
            return detailedCollection.isEditable;
        }
        return storeData.checkEditability(collection, currentUser?.uid || '');
    }, [storeData.allCollections, storeData.checkEditability, currentUser?.uid]);

    // Filtered collections
    const getFilteredCollections = useMemo(() => {
        if (collectionType === CollectionType.CURATED) {
            return storeData.curatedCollections || [];
        }

        // Check if any filters are active
        const hasProjectFilters = selectedProjectIds.length > 0;
        const hasIndividualFilters = individualCollectionFilters.myIndividual || individualCollectionFilters.sharedIndividual;

        // If no filters are active, show all collections
        if (!hasProjectFilters && !hasIndividualFilters) {
            return storeData.allCollections || [];
        }

        // Use OR logic: include collections that match ANY of the active filters
        const collectionsToInclude = new Set<CollectionInterface>();

        // Add project collections if project filters are active
        if (hasProjectFilters) {
            const projectFilteredCollections = projectCollections.filter((collection) => 
                collection.associatedProjectId && selectedProjectIds.includes(collection.associatedProjectId)
            );
            projectFilteredCollections.forEach(collection => collectionsToInclude.add(collection));
        }

        // Add individual collections if individual filters are active
        if (hasIndividualFilters) {
            if (individualCollectionFilters.myIndividual) {
                storeData.myIndividualCollections.forEach(collection => collectionsToInclude.add(collection));
            }
            if (individualCollectionFilters.sharedIndividual) {
                storeData.sharedIndividualCollections.forEach(collection => collectionsToInclude.add(collection));
            }
        }

        return Array.from(collectionsToInclude);
    }, [
        collectionType, 
        storeData.curatedCollections, 
        storeData.allCollections,
        projectCollections,
        selectedProjectIds, 
        individualCollectionFilters,
        storeData.myIndividualCollections,
        storeData.sharedIndividualCollections
    ]);

    // Simplified getItems with single guard
    const getItems = useCallback(async () => {       
        if (collectionType === CollectionType.MY) {
            // Single guard: don't fetch if already fetching or collections are loaded
            if (isFetchingRef.current || (storeData.allCollections.length > 0)) {
                return Promise.resolve();
            }
            
            isFetchingRef.current = true;
            try {
                return await storeData.getMoreCollections('all');
            } finally {
                isFetchingRef.current = false;
            }
        } else if (!storeData.curatedCollectionsLoaded) {
            return storeData.getMoreCuratedCollections();
        }
        return Promise.resolve();
    }, [collectionType, storeData.getMoreCollections, storeData.curatedCollectionsLoaded, storeData.getMoreCuratedCollections, storeData.allCollections]);

    // Event handlers
    const handleMouseOver = useCallback((e, id) => {
        e.preventDefault();
        const d = document.getElementById(id + 'delIcon');
        if (d) d.style.visibility = 'visible';
        const ee = document.getElementById(id + 'editIcon');
        if (ee) ee.style.visibility = 'visible';
    }, []);

    const handleMouseOut = useCallback((e, id) => {
        e.preventDefault();
        const d = document.getElementById(id + 'delIcon');
        if (d) d.style.visibility = 'hidden';   
        const ed = document.getElementById(id + 'editIcon');
        if (ed) ed.style.visibility = 'hidden';
    }, []);

    const editIconClick = useCallback((e, collection) => {
        useMattoState.getState().setModal({name:'collection', id:collection.id, product:null, userImage:null, userMaterial: null});   
    }, []);

    const deleteIconOnclick = useCallback(async (e, collection) => {
        // Handle case where event might be null (called from CollectionItemCover)
        if (e && e.stopPropagation) {
            e.stopPropagation();
        }
        if (window.confirm('Are you sure you wish to delete this collection? This action cannot be undone.')) {
            try {
                await storeData.deleteCollection(collection, currentUser?.uid);
            } catch (error) {
                console.error('Error deleting collection:', error);
               
                let errorMessage = 'Failed to delete collection';
                if (error.message) {
                    if (error.message.includes('permission')) {
                        errorMessage = 'You don\'t have permission to delete this collection';
                    } else if (error.message.includes('not found')) {
                        errorMessage = 'Collection not found or already deleted';
                    } else {
                        errorMessage = error.message;
                    }
                }
               
                alert(errorMessage);
            }
        }
    }, [storeData.deleteCollection, currentUser?.uid]);


    return (
        <>
            {/* Filter components for My Collections */}
            {collectionType === CollectionType.MY && currentUser?.uid && (
                <SearchFilter boundingBox={rootCollectionContainer} side='left' typesenseFilter={false} disabled={!isProUser && !isTeamsProUser}>
                    <ProjectFilter
                        onProjectFiltersChange={onSelectedProjectIdsChange}
                        onIndividualCollectionFiltersChange={onIndividualFiltersChange}
                        collections={storeData.allCollections}
                        isTeamsProUser={isTeamsProUser}
                        isRegularProUser={isProUser}
                    />
                </SearchFilter>
            )}

            {/* Main collection display */}
            <Grid item xs={12} style={{paddingLeft:'8px',paddingTop:'5px', height: 'calc(100vh - 200px)'}}>
                <ScrollableGrid
                    style={{maxHeight:'calc(100vh - 200px)', overflow:'auto', paddingTop:'10px', paddingLeft:'4px'}}
                    loadItems={getItems}
                    onClick={onCollectionSelect}
                    editIconOnClick={editIconClick}
                    deleteIconOnclick={deleteIconOnclick}
                    onMouseOver={handleMouseOver}
                    onMouseOut={handleMouseOut}
                    getNoOfItems={getNoOfItems}
                    canEditOrShare={canEditOrShareCollection}
                    items={getFilteredCollections}
                    type={collectionType === CollectionType.CURATED ? 'curatedImages' : 'collections'}
                    draggable={false}
                    isTeamsProUser={isTeamsProUser}
                    isProUser={isProUser}
                />
            </Grid>
        </>
    );
});


export function CollectionList({selectedCollection, onClose, collectionType, canEditCollection}) {
   const [productHitsArray, setProductHitsArray]=useState([])
   const [currentCollection, setCurrentCollection] = useState(selectedCollection)

   
   // Listen for collection updates and sync with local state
   const allCollections = useCollectionDetailed(state => state.allCollections);
   const loginStateCollections = useLoginState(state => state.collections);
   
   useEffect(() => {
       if (selectedCollection) {
           // Try to find the updated collection from either store
           let updatedCollection: CollectionInterface | undefined = allCollections.find(c => c.id === selectedCollection.id);
           
           // Fallback to LoginState if not found in detailed store
           if (!updatedCollection) {
               updatedCollection = loginStateCollections.find((c: CollectionInterface) => c.id === selectedCollection.id);
           }
           
           if (updatedCollection) {
               setCurrentCollection(updatedCollection);
           } else {
               setCurrentCollection(selectedCollection);
           }
       }
   }, [selectedCollection, allCollections, loginStateCollections]);
   
   const areTwoArrayOfObjectsSame = (first_array_of_objects,second_array_of_objects) => {
       return (
           first_array_of_objects.length === second_array_of_objects.length &&
           first_array_of_objects.every((element_1) =>
               second_array_of_objects.some(
                   (element_2) =>
                       element_1.copyFromID === element_2.copyFromID
               )
           )
       );
   };
   
   const getProductsHitsFromTypesenseSearch = (data) =>{
       const productTypeData = data.filter((hit) => hit.productType==ProductType.MATERIAL)
       const sameDataReceived = areTwoArrayOfObjectsSame(productHitsArray,productTypeData)
         if(!sameDataReceived){
           setProductHitsArray(productTypeData)
         }
   }
   
   return (
       <Stack sx={{width:'100%',paddingTop:'30px',paddingLeft:'0px',display:selectedCollection!=null ?'flex':'none', maxHeight: 'calc(100vh - 100px)'}}>
           <Stack direction={'row'} spacing={1} onClick={onClose} style={{cursor:'pointer'}}>
               <ArrowBack  style={{paddingLeft:'10px',paddingBottom:'10px'}} />
               <Typography>Back</Typography>
           </Stack>

           <TypesenseSearch
               indexName='productsV2'
               productType={ProductType.ANY}
               hitsHandler={CollectionHits}
               selectedCollection={currentCollection}
               getProductsHitsFromTypesenseSearch={getProductsHitsFromTypesenseSearch}
               collectionType={collectionType}
               canEditCollection={canEditCollection}
           />
       </Stack>
   )
}
